import React, { useEffect, useState } from "react";
import { apiGet, apiPost, apiPut, formatDateTime } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Settings2, Mail, Send, Bell, Crown, Loader2, Save, AlertTriangle,
  CheckCircle2, BookOpen, ChevronDown, AppWindow, Eye, EyeOff,
  Briefcase, Percent, ShieldCheck, XCircle, ExternalLink,
} from "lucide-react";
import MarketStatusCard from "@/components/admin/MarketStatusCard";
import LlmCostsCard from "@/components/admin/LlmCostsCard";
import { toast } from "sonner";

const SettingsPage = () => {
  // Issue #14 — honour #email-configuration hash by scrolling into view.
  useEffect(() => {
    const hash = (window.location.hash || "").replace(/^#/, "");
    if (!hash) return;
    const t = setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        el.classList.add("ring-2", "ring-primary/40");
        setTimeout(() => el.classList.remove("ring-2", "ring-primary/40"), 2500);
      }
    }, 350);
    return () => clearTimeout(t);
  }, []);

  // SMTP (DB-backed editable form)
  const [smtpForm, setSmtpForm] = useState({
    smtp_username: "",
    smtp_password: "",
    smtp_from_name: "CSE Investor",
  });
  const [smtpHasPassword, setSmtpHasPassword] = useState(false);
  const [smtpStatus, setSmtpStatus] = useState(null); // { configured, dev_mode, source, ... }
  const [showPw, setShowPw] = useState(false);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  // Issue #17 — verify-only (no email sent) state
  const [verifyingSmtp, setVerifyingSmtp] = useState(false);
  const [smtpAuthError, setSmtpAuthError] = useState(null); // {message, hint}

  // Generic settings
  const [appSettings, setAppSettings] = useState({});
  const [adminEmail, setAdminEmail] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingApp, setSavingApp] = useState(false);
  const [savingTrial, setSavingTrial] = useState(false);
  const [appName, setAppName] = useState("");
  const [appTagline, setAppTagline] = useState("");
  const [trialDays, setTrialDays] = useState("14");
  const [guideOpen, setGuideOpen] = useState(false);

  // Stage 1 — Portfolio brokerage rates (decimals; UI shows %)
  const [buyRate, setBuyRate] = useState("1.21");
  const [sellRate, setSellRate] = useState("1.21");
  const [savingBrokerage, setSavingBrokerage] = useState(false);
  const [backfillBusy, setBackfillBusy] = useState(false);

  const loadAll = () => {
    apiGet("/admin/smtp/config")
      .then((c) => {
        setSmtpForm({
          smtp_username: c.smtp_username || "",
          smtp_password: c.smtp_password_masked || "",
          smtp_from_name: c.smtp_from_name || "CSE Investor",
        });
        setSmtpHasPassword(!!c.has_password);
        setSmtpStatus(c.status || null);
      })
      .catch(() => {});
    apiGet("/admin/settings")
      .then((s) => {
        setAppSettings(s);
        setAdminEmail(s.admin_notification_email || "");
        setAppName(s.app_name || "CSE Investor");
        setAppTagline(s.app_tagline || "");
        setTrialDays(String(s.default_trial_days || 14));
        // Stage 1 — brokerage rates (stored as decimals, UI as %)
        if (s.brokerage_buy_rate != null) {
          setBuyRate(((s.brokerage_buy_rate || 0) * 100).toFixed(2));
        }
        if (s.brokerage_sell_rate != null) {
          setSellRate(((s.brokerage_sell_rate || 0) * 100).toFixed(2));
        }
      })
      .catch(() => {});
  };

  useEffect(loadAll, []);

  const saveSmtp = async () => {
    setSavingSmtp(true);
    try {
      const body = {
        smtp_username: smtpForm.smtp_username.trim(),
        smtp_from_name: smtpForm.smtp_from_name.trim() || "CSE Investor",
      };
      // Only send password if it's been changed (not the masked placeholder)
      if (smtpForm.smtp_password && !smtpForm.smtp_password.includes("****")) {
        body.smtp_password = smtpForm.smtp_password;
      }
      const res = await apiPut("/admin/smtp/config", body);
      setSmtpStatus(res?.status || null);
      toast.success("Email settings saved", {
        description: res?.status?.configured
          ? "SMTP credentials stored. Use 'Send Test Email' to verify delivery."
          : "Saved — but SMTP is still incomplete (username, password, or display name missing).",
      });
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingSmtp(false);
    }
  };

  const sendSmtpTest = async () => {
    if (!adminEmail) {
      toast.error("Save an admin notification email below before testing.");
      return;
    }
    setTestingSmtp(true);
    setSmtpAuthError(null);
    try {
      // Use the form values for a live test (no save) when password was changed
      const live =
        smtpForm.smtp_username && smtpForm.smtp_password &&
        !smtpForm.smtp_password.includes("****");
      const body = live
        ? {
            smtp_username: smtpForm.smtp_username.trim(),
            smtp_password: smtpForm.smtp_password,
            smtp_from_name: smtpForm.smtp_from_name,
            to: adminEmail,
          }
        : { to: adminEmail };
      const res = await apiPost("/admin/smtp/test", body);
      if (res?.ok) {
        toast.success(`Test email sent to ${res?.to || adminEmail}`);
      } else {
        // Issue #17 — surface friendly auth-failure inline
        const raw = res?.error || "";
        if (/535|Username and Password not accepted|SMTPAuthenticationError/i.test(raw)) {
          setSmtpAuthError({
            message: "Gmail rejected your credentials (535 — Username and Password not accepted).",
            hint:
              "Your Gmail App Password may have expired or been revoked. Regenerate a new 16-character App Password and paste it above.",
          });
          toast.error("Gmail authentication failed", {
            description: "Your App Password was rejected — see the red panel above for next steps.",
            duration: 9000,
          });
        } else {
          toast.error("Test send failed", {
            description: raw || "Check Email Logs for details.",
            duration: 9000,
          });
        }
      }
      // Refresh status badge
      apiGet("/admin/smtp/config").then((c) => setSmtpStatus(c?.status || null));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send test");
    } finally {
      setTestingSmtp(false);
    }
  };

  // Issue #17 — credential-only verify (no email sent)
  const verifySmtp = async () => {
    setVerifyingSmtp(true);
    setSmtpAuthError(null);
    try {
      const live =
        smtpForm.smtp_username && smtpForm.smtp_password &&
        !smtpForm.smtp_password.includes("****");
      const body = live
        ? {
            smtp_username: smtpForm.smtp_username.trim(),
            smtp_password: smtpForm.smtp_password,
          }
        : {};
      const res = await apiPost("/admin/smtp/verify", body);
      if (res?.ok) {
        toast.success("Gmail credentials accepted", {
          description: "SMTP login handshake succeeded. You can now save and send a test email.",
        });
      } else {
        const raw = res?.error || "";
        if (res?.hint || /535|Username and Password not accepted|SMTPAuthenticationError/i.test(raw)) {
          setSmtpAuthError({
            message: "Gmail authentication failed (535 — Username and Password not accepted).",
            hint:
              res?.hint ||
              "Your Gmail App Password may have expired or been revoked. Regenerate a new 16-character App Password and paste it above.",
          });
        }
        toast.error("Credential check failed", {
          description: raw || "See details above.",
          duration: 9000,
        });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally {
      setVerifyingSmtp(false);
    }
  };

  const saveAdminEmail = async () => {
    setSavingEmail(true);
    try {
      await apiPut("/admin/settings", { admin_notification_email: adminEmail });
      toast.success("Admin notification email saved");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingEmail(false); }
  };

  const saveAppSettings = async () => {
    setSavingApp(true);
    try {
      const res = await apiPut("/admin/settings", {
        app_name: appName.trim(),
        app_tagline: appTagline.trim(),
      });
      toast.success("Application settings saved");
      setAppSettings(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingApp(false); }
  };

  const saveTrialDays = async () => {
    setSavingTrial(true);
    try {
      const res = await apiPut("/admin/settings", {
        default_trial_days: Number(trialDays),
      });
      toast.success(`Default trial duration set to ${res.default_trial_days} days`);
      setAppSettings(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingTrial(false); }
  };

  // Stage 1 — Brokerage rates
  const saveBrokerage = async () => {
    const buyDec = Number(buyRate) / 100;
    const sellDec = Number(sellRate) / 100;
    if (
      Number.isNaN(buyDec) || Number.isNaN(sellDec) ||
      buyDec < 0 || buyDec > 0.10 || sellDec < 0 || sellDec > 0.10
    ) {
      toast.error("Rates must be between 0% and 10%");
      return;
    }
    setSavingBrokerage(true);
    try {
      const res = await apiPut("/admin/settings", {
        brokerage_buy_rate: buyDec,
        brokerage_sell_rate: sellDec,
      });
      toast.success(`Brokerage rates saved (${(res.brokerage_buy_rate * 100).toFixed(2)}% buy / ${(res.brokerage_sell_rate * 100).toFixed(2)}% sell)`);
      setAppSettings(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingBrokerage(false);
    }
  };

  const runBrokerageBackfill = async () => {
    if (!window.confirm(
      "Run retroactive brokerage backfill on ALL existing holdings? " +
      "This sets brokerage = purchase_price × shares × current rate for any " +
      "holding without a stored value, and tags them as 'retroactive'."
    )) return;
    setBackfillBusy(true);
    try {
      const res = await apiPost(
        `/admin/portfolio-settings/backfill-brokerage?dry_run=false`, {}
      );
      toast.success(`Backfill complete — ${res.patched} of ${res.examined} holdings updated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfillBusy(false);
    }
  };

  const smtpOk = smtpStatus?.configured;
  const smtpSource = smtpStatus?.source; // 'db' | 'env' | 'none'

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-display text-xl font-semibold flex items-center gap-2">
          <Settings2 className="h-5 w-5 text-primary" />
          Settings
        </h1>
        <p className="text-sm text-muted-foreground">
          Email delivery, admin notifications, application branding, subscription policy and the CSE holiday calendar.
        </p>
      </div>

      {/* ---------- 1. Email Configuration (SMTP, EDITABLE) ---------- */}
      <Card
        className="border bg-card"
        data-testid="smtp-config-card"
        id="email-configuration"
      >
        <CardHeader className="pb-3">
          <CardTitle className="font-display flex items-center gap-2">
            <Mail className="h-4 w-4 text-primary" />
            Email Configuration (SMTP)
          </CardTitle>
          <CardDescription>
            Configure outgoing email directly from the UI — credentials are stored in
            the database and applied immediately without restarting the backend.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Status badge */}
          <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/30 px-3 py-2.5">
            {smtpOk ? (
              <Badge variant="outline" className="bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/30">
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                SMTP configured
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30">
                <AlertTriangle className="h-3.5 w-3.5 mr-1" />
                SMTP not configured
              </Badge>
            )}
            {smtpSource && (
              <Badge variant="secondary" className="text-[10px]">
                source: {smtpSource}
              </Badge>
            )}
            {smtpStatus?.dev_mode && (
              <Badge variant="outline" className="text-[10px] text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40">
                dev mode (emails logged only)
              </Badge>
            )}
            {smtpStatus?.username && (
              <span className="text-xs text-muted-foreground font-mono truncate max-w-[280px]" title={smtpStatus.username}>
                {smtpStatus.username}
              </span>
            )}
          </div>

          {/* Editable form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="smtp-username">Gmail Address (MAIL_USERNAME)</Label>
              <Input
                id="smtp-username"
                type="email"
                value={smtpForm.smtp_username}
                onChange={(e) => setSmtpForm((s) => ({ ...s, smtp_username: e.target.value }))}
                placeholder="your@gmail.com"
                data-testid="smtp-username-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="smtp-password">
                Gmail App Password
                {smtpHasPassword && (
                  <span className="ml-2 text-[10px] text-muted-foreground">
                    (leave unchanged to keep current)
                  </span>
                )}
              </Label>
              <div className="relative">
                <Input
                  id="smtp-password"
                  type={showPw ? "text" : "password"}
                  value={smtpForm.smtp_password}
                  onChange={(e) => setSmtpForm((s) => ({ ...s, smtp_password: e.target.value }))}
                  placeholder="16-character app password from Google"
                  className="pr-10 font-mono text-xs"
                  data-testid="smtp-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  data-testid="smtp-password-toggle"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="smtp-from-name">Display Name (MAIL_FROM_NAME)</Label>
              <Input
                id="smtp-from-name"
                value={smtpForm.smtp_from_name}
                onChange={(e) => setSmtpForm((s) => ({ ...s, smtp_from_name: e.target.value }))}
                placeholder="CSE Investor"
                data-testid="smtp-from-name-input"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={saveSmtp} disabled={savingSmtp} className="gap-2" data-testid="smtp-save">
              {savingSmtp ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save Email Settings
            </Button>
            <Button
              variant="outline"
              onClick={verifySmtp}
              disabled={verifyingSmtp || !smtpForm.smtp_username || (!smtpForm.smtp_password && !smtpHasPassword)}
              className="gap-2"
              data-testid="smtp-verify"
              title="Test the SMTP login handshake only (does not send an email)"
            >
              {verifyingSmtp ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
              Test connection
            </Button>
            <Button
              variant="outline"
              onClick={sendSmtpTest}
              disabled={testingSmtp || !smtpForm.smtp_username || (!smtpForm.smtp_password && !smtpHasPassword)}
              className="gap-2"
              data-testid="smtp-test"
            >
              {testingSmtp ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Send Test Email
            </Button>
          </div>

          {/* Issue #17 — Friendly inline auth-failure alert */}
          {smtpAuthError && (
            <div
              className="rounded-md border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 p-4 space-y-2"
              data-testid="smtp-auth-error-alert"
              role="alert"
            >
              <div className="flex items-start gap-2">
                <XCircle className="h-4 w-4 text-[hsl(var(--destructive))] shrink-0 mt-0.5" />
                <div className="text-sm">
                  <div className="font-semibold text-[hsl(var(--destructive))]">
                    {smtpAuthError.message}
                  </div>
                  {smtpAuthError.hint && (
                    <p className="mt-1 text-xs text-foreground/80 leading-relaxed">
                      {smtpAuthError.hint}
                    </p>
                  )}
                </div>
              </div>
              <div className="rounded-md bg-background/60 border p-3 text-xs space-y-1.5">
                <div className="font-medium text-foreground">Regenerate your Gmail App Password:</div>
                <ol className="list-decimal list-inside space-y-1 text-muted-foreground leading-relaxed">
                  <li>
                    Go to{" "}
                    <a
                      className="text-primary underline-offset-2 hover:underline inline-flex items-center gap-0.5"
                      href="https://myaccount.google.com/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      myaccount.google.com <ExternalLink className="h-3 w-3" />
                    </a>
                  </li>
                  <li>Open <strong>Security</strong> &rarr; <strong>2-Step Verification</strong></li>
                  <li>Scroll to the bottom and open <strong>App passwords</strong></li>
                  <li>Delete the old &ldquo;CSE Investor&rdquo; password</li>
                  <li>Create a new App Password (select <em>Mail</em> / <em>Other</em>)</li>
                  <li>Paste the new 16-character password above and click <strong>Save Email Settings</strong></li>
                  <li>Click <strong>Test connection</strong> to confirm it works</li>
                </ol>
              </div>
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setSmtpAuthError(null)}
                  data-testid="smtp-auth-error-dismiss"
                >
                  Dismiss
                </Button>
              </div>
            </div>
          )}

          <Collapsible open={guideOpen} onOpenChange={setGuideOpen}>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="w-full flex items-center justify-between rounded-md border bg-muted/30 hover:bg-muted/50 transition-colors px-3 py-2 text-xs font-medium"
                data-testid="settings-smtp-guide-toggle"
              >
                <span className="inline-flex items-center gap-2">
                  <BookOpen className="h-3.5 w-3.5 text-primary" />
                  How to get a Gmail App Password
                </span>
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${guideOpen ? "rotate-180" : ""}`} />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="rounded-md border bg-card px-4 py-3 mt-2 space-y-2 text-xs">
                <ol className="list-decimal list-inside space-y-1.5 text-muted-foreground leading-relaxed">
                  <li>
                    Enable 2-Step Verification at{" "}
                    <a className="text-primary underline-offset-2 hover:underline" href="https://myaccount.google.com/security" target="_blank" rel="noreferrer">
                      myaccount.google.com/security
                    </a>
                  </li>
                  <li>
                    Open{" "}
                    <a className="text-primary underline-offset-2 hover:underline" href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer">
                      Google App Passwords
                    </a>
                  </li>
                  <li>Generate a new password for <em>Mail</em></li>
                  <li>Paste the 16-character code into the <strong>Gmail App Password</strong> field above</li>
                  <li>Click <strong>Save Email Settings</strong>, then <strong>Send Test Email</strong> to verify</li>
                </ol>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>

      {/* ---------- 2. Admin Notifications ---------- */}
      <Card className="border-2 border-primary/20 bg-card" data-testid="admin-notifications-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-display flex items-center gap-2">
            <Bell className="h-4 w-4 text-primary" /> Admin Notifications
          </CardTitle>
          <CardDescription>
            All user registration, approval, and system alerts are sent to this address.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!adminEmail ? (
            <div className="flex items-start gap-2 rounded-md border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-3 py-2.5 text-xs text-[hsl(var(--warning))]">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold">No notification email set</div>
                <div className="opacity-90 mt-0.5">Admin alerts will not be sent until you configure an address below.</div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-xs">
              <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--success))]" />
              <span className="text-muted-foreground">Currently:</span>
              <span className="font-mono font-medium text-foreground">{adminEmail}</span>
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="admin-email">Email address</Label>
              <Input
                id="admin-email"
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                placeholder="admin@yourdomain.com"
                data-testid="admin-notification-email-input"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={saveAdminEmail} disabled={savingEmail} className="gap-2" data-testid="admin-notification-email-save">
                {savingEmail ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                Save
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ---------- 3. Application Settings ---------- */}
      <Card className="border bg-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-display flex items-center gap-2">
            <AppWindow className="h-4 w-4 text-primary" />
            Application Settings
          </CardTitle>
          <CardDescription>Branding shown on the login screen, sidebar and outgoing emails.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="app-name">App name</Label>
            <Input id="app-name" value={appName} onChange={(e) => setAppName(e.target.value)} data-testid="app-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="app-tagline">App tagline</Label>
            <Input id="app-tagline" value={appTagline} onChange={(e) => setAppTagline(e.target.value)} data-testid="app-tagline-input" />
          </div>
          <div className="flex justify-end">
            <Button onClick={saveAppSettings} disabled={savingApp} className="gap-2" data-testid="app-settings-save">
              {savingApp ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ---------- 4. Subscription Settings ---------- */}
      <Card className="border bg-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-display flex items-center gap-2">
            <Crown className="h-4 w-4 text-[hsl(var(--warning))]" />
            Subscription Settings
          </CardTitle>
          <CardDescription>
            How long new users get free Premium access when their registration is approved.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
            <div className="space-y-1.5">
              <Label>Default trial duration</Label>
              <Select value={trialDays} onValueChange={setTrialDays}>
                <SelectTrigger className="h-9" data-testid="trial-days-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days (recommended)</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end">
              <Button onClick={saveTrialDays} disabled={savingTrial} className="gap-2" data-testid="trial-days-save">
                {savingTrial ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                Save
              </Button>
            </div>
          </div>
          {appSettings.updated_at && (
            <p className="text-[11px] text-muted-foreground">
              Last updated: {formatDateTime(appSettings.updated_at)}
            </p>
          )}
        </CardContent>
      </Card>

      {/* ---------- 4b. Portfolio (Brokerage) Settings ---------- */}
      <Card className="border bg-card" data-testid="portfolio-settings-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-display flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-primary" />
            Portfolio Settings
          </CardTitle>
          <CardDescription>
            Brokerage commission rates used in every cost, breakeven and projection
            calculation. CSE standard is 1.21% on both buy and sell. Update if your
            broker charges differ.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="brokerage-buy" className="flex items-center gap-1.5">
                <Percent className="h-3.5 w-3.5 text-muted-foreground" />
                Buy commission rate (%)
              </Label>
              <Input
                id="brokerage-buy"
                type="number"
                min="0"
                max="10"
                step="0.01"
                value={buyRate}
                onChange={(e) => setBuyRate(e.target.value)}
                data-testid="brokerage-buy-input"
              />
              <p className="text-[11px] text-muted-foreground">
                Rs.100,000 trade × {(Number(buyRate) || 0).toFixed(2)}% = Rs.{(
                  (Number(buyRate) || 0) * 1000
                ).toLocaleString(undefined, { maximumFractionDigits: 0 })} brokerage
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="brokerage-sell" className="flex items-center gap-1.5">
                <Percent className="h-3.5 w-3.5 text-muted-foreground" />
                Sell commission rate (%)
              </Label>
              <Input
                id="brokerage-sell"
                type="number"
                min="0"
                max="10"
                step="0.01"
                value={sellRate}
                onChange={(e) => setSellRate(e.target.value)}
                data-testid="brokerage-sell-input"
              />
              <p className="text-[11px] text-muted-foreground">
                Round-trip cost: ~{((Number(buyRate) || 0) + (Number(sellRate) || 0)).toFixed(2)}%
                of trade value
              </p>
            </div>
          </div>
          <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
            <Button
              variant="outline"
              size="sm"
              onClick={runBrokerageBackfill}
              disabled={backfillBusy}
              className="gap-1.5"
              data-testid="brokerage-backfill-button"
            >
              {backfillBusy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <CheckCircle2 className="h-3.5 w-3.5" />
              )}
              Backfill existing holdings
            </Button>
            <Button
              onClick={saveBrokerage}
              disabled={savingBrokerage}
              className="gap-2"
              data-testid="brokerage-save-button"
            >
              {savingBrokerage ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save rates
            </Button>
          </div>
          <div className="rounded-md bg-muted/40 border border-border p-3 text-xs space-y-1.5">
            <div className="flex items-start gap-2">
              <span className="text-[hsl(var(--success))] font-bold mt-px">✓</span>
              <div>
                <span className="font-medium text-foreground">Capital gains tax: None.</span>{" "}
                Profits from selling CSE listed shares are exempt from capital gains
                tax in Sri Lanka — you keep 100% of your profit when you sell.
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-muted-foreground font-bold mt-px">%</span>
              <div>
                <span className="font-medium text-foreground">Dividend withholding tax: 10%.</span>{" "}
                Set by Sri Lanka law and deducted at source by the company before payment
                — not user-editable. The app shows dividend income net of WHT throughout.
              </div>
            </div>
            <div className="text-muted-foreground pl-6">
              The backfill button retroactively computes brokerage on existing holdings
              at the current rate (tagged as <code>brokerage_source: retroactive</code>).
              New holdings are tagged as <code>calculated</code>.
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ---------- 5. Market Holidays ---------- */}
      <MarketStatusCard />
      <LlmCostsCard />
    </div>
  );
};

export default SettingsPage;
